import { Injectable } from "@angular/core"
import { Observable, of } from "rxjs"
import { catchError, shareReplay } from "rxjs/operators"

import { LmApiService } from "./lm-api.service"
import {
    ISchool,
    ISchoolDistrict,
    IState,
    SCHOOL_TYPE_DESCRIPTION,
    UserDataPrivacyPayload,
    UserDataPrivacyResponse,
} from "../models/userDataPrivacy.model"
import { ApiResponse, IApiResponse } from "../models/apiResponse.model"
import { AssignmentResponse } from "../models/teacherAssignments.model"

@Injectable({
    providedIn: "root",
})
export class UserDataPrivacyService {
    private baseUrl = "/api/v2"
    private cachedStates$: Observable<IApiResponse<IState>>

    constructor(private lmApi: LmApiService) {}

    getSchoolDistricts(
        stateLocation = "",
        searchQuery = "",
        limit = 30
    ): Observable<IApiResponse<ISchoolDistrict>> {
        let url = `${this.baseUrl}/tools/school-district/?limit=${limit}`
        url = stateLocation ? `${url}&state=${stateLocation}` : url
        url = searchQuery ? `${url}&search=${searchQuery}` : url

        return this.lmApi.get(url).pipe(
            catchError((error) => this.lmApi.handleErrorForNonCritical(error)),
            catchError(() => of(new ApiResponse<ISchoolDistrict>()))
        )
    }

    getSchools(districtId = "", searchQuery = "", limit = 30): Observable<IApiResponse<ISchool>> {
        if (!districtId) return

        let url = `${this.baseUrl}/tools/schools-by-district/${districtId}/?limit=${limit}`
        url = searchQuery ? `${url}&search=${searchQuery}` : url

        return this.lmApi.get(url).pipe(
            catchError((error) => this.lmApi.handleErrorForNonCritical(error)),
            catchError(() => of(new ApiResponse<ISchool>()))
        )
    }

    getDataByNextUrl<T>(nextUrl): Observable<IApiResponse<T>> {
        if (!nextUrl) return
        return this.lmApi.get(nextUrl).pipe(
            catchError((error) => this.lmApi.handleErrorForNonCritical(error)),
            catchError(() => of(new ApiResponse<T>()))
        )
    }

    getStates(): Observable<IApiResponse<IState>> {
        if (!this.cachedStates$) {
            this.cachedStates$ = this.lmApi.get(`${this.baseUrl}/tools/states/`).pipe(
                catchError((error) => this.lmApi.handleErrorForNonCritical(error)),
                catchError(() => of(new ApiResponse<IState>())),
                shareReplay(1)
            )
        }

        return this.cachedStates$
    }

    getUserDataPrivacy(): Observable<UserDataPrivacyResponse> {
        return this.lmApi
            .get(`${this.baseUrl}/user_data_privacy/`)
            .pipe(catchError((error) => this.lmApi.handleErrorForNonCritical(error)))
    }

    setDataPrivacy<T>(payload: T): Observable<any> {
        const url = `${this.baseUrl}/tools/set-data-privacy/`
        return this.lmApi
            .post(url, payload)
            .pipe(catchError((error) => this.lmApi.handleErrorForNonCritical(error.error.detail)))
    }

    getAssignments(): Observable<IApiResponse<AssignmentResponse>> {
        return this.lmApi.get(`${this.baseUrl}/assignments/`).pipe(
            catchError((error) => this.lmApi.handleErrorForNonCritical(error.error.message)),
            catchError(() => of(new ApiResponse<AssignmentResponse>()))
        )
    }

    createPayload({
        privacyCheck: sign_acknowledgement,
        classRoomSetting: { schoolDescription, teacherType },
    }): UserDataPrivacyPayload {
        const schoolDistrict = schoolDescription?.schoolDistrict ?? null
        const schoolName = schoolDescription?.schoolName ?? null

        return {
            sign_acknowledgement,
            teacher_type: SCHOOL_TYPE_DESCRIPTION[teacherType],
            ...(schoolDistrict?.district_guid && {
                district_guid: schoolDistrict.district_guid,
            }),
            ...(schoolName?.id && {
                school_id: schoolName.id,
            }),
        }
    }
}
